/**
 * ZRF-FIN client API helpers (flag-gated on server via FINANCIALS_ENABLED).
 */

import api from '../../api';

export async function createLedgerFromLease(leaseId, body = {}) {
  const res = await api.post(`/api/leases/${leaseId}/create-ledger`, body);
  return res.data;
}

export async function getTenantLedger(tenantId, propertyId, params = {}) {
  const res = await api.get(`/api/tenants/${tenantId}/ledger`, {
    params: { propertyId, ...params }
  });
  return res.data;
}

/** Tenant portal: ledger for the signed-in tenant (tenant session; property comes from the tenant record). */
export async function getTenantPortalLedger(tenantId) {
  const res = await api.get(`/api/tenant-portal/tenant/${tenantId}/ledger`);
  return res.data;
}

export async function downloadTenantPortalReceiptPdf(tenantId, receiptId) {
  const res = await api.get(`/api/tenant-portal/tenant/${tenantId}/receipts/${receiptId}/pdf`, {
    responseType: 'blob'
  });
  return res.data;
}

export async function getFinancialSummary(params = {}) {
  const res = await api.get('/api/financials/summary', { params });
  return res.data;
}

/** Categories (with suggested account names) and the saved QuickBooks account names. */
export async function getAccountingMapping() {
  const res = await api.get('/api/financials/accounting/mapping');
  return res.data;
}

/** @param {Record<string, string>} accounts category id → QuickBooks account name */
export async function saveAccountingMapping(accounts) {
  const res = await api.put('/api/financials/accounting/mapping', { accounts });
  return res.data;
}

/** @param {{ from?: string, to?: string, propertyId?: string }} params */
export async function getAccountingPreview(params = {}) {
  const res = await api.get('/api/financials/accounting/preview', { params });
  return res.data;
}

const QB = '/api/integrations/quickbooks';

/** { configured, environment, connected, status, companyName?, connectedAt? } */
export async function getQuickBooksStatus() {
  const res = await api.get(`${QB}/status`);
  return res.data;
}

/** Starts Intuit sign-in; returns { authUrl }. */
export async function connectQuickBooks() {
  const res = await api.post(`${QB}/connect`);
  return res.data;
}

export async function disconnectQuickBooks() {
  const res = await api.post(`${QB}/disconnect`);
  return res.data;
}

/** Active accounts in the connected company: [{ id, name, fullName, type, classification }] */
export async function getQuickBooksAccounts() {
  const res = await api.get(`${QB}/accounts`);
  return res.data?.accounts || [];
}

/** Months already sent to the connected company, newest first. */
export async function getQuickBooksSyncs() {
  const res = await api.get(`${QB}/syncs`);
  return res.data?.syncs || [];
}

/** @param {{ from: string, to: string, propertyId?: string }} body */
export async function sendToQuickBooks(body) {
  const res = await api.post(`${QB}/send`, body);
  return res.data?.sync;
}

export async function recordManualPayment(body = {}) {
  const res = await api.post('/api/payments/manual', body);
  return res.data;
}

export async function voidPayment(paymentId, body = {}) {
  const res = await api.post(`/api/payments/${paymentId}/void`, body);
  return res.data;
}

export async function collectDeposit(depositId, body = {}) {
  const res = await api.post(`/api/deposits/${depositId}/collect`, body);
  return res.data;
}

export async function applyDeposit(depositId, body = {}) {
  const res = await api.post(`/api/deposits/${depositId}/apply`, body);
  return res.data;
}

export async function refundDeposit(depositId, body = {}) {
  const res = await api.post(`/api/deposits/${depositId}/refund`, body);
  return res.data;
}

/**
 * Download receipt PDF (blob). Caller should revoke object URL when done.
 */
export async function downloadReceiptPdf(receiptId) {
  const res = await api.get(`/api/receipts/${receiptId}/pdf`, {
    responseType: 'blob'
  });
  return res.data;
}

/**
 * Download CSV export (blob). Opens as file download.
 * @param {{ type: string, propertyId?: string, tenantId?: string, from?: string, to?: string }} params
 */
export async function exportFinancialsCsv(params = {}) {
  const res = await api.get('/api/financials/export', {
    params,
    responseType: 'blob'
  });
  const disposition = res.headers?.['content-disposition'] || '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] || `financials-${params.type || 'export'}.csv`;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return filename;
}

/** sessionStorage key for deep-linking into Financial Center ledger view */
export const FIN_LEDGER_NAV_KEY = 'zrf-fin-open-ledger';

export function stashLedgerNav(detail) {
  try {
    sessionStorage.setItem(FIN_LEDGER_NAV_KEY, JSON.stringify(detail));
  } catch (_) {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('zrf-fin-open-ledger', { detail }));
  }
}

export function consumeLedgerNav() {
  try {
    const raw = sessionStorage.getItem(FIN_LEDGER_NAV_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(FIN_LEDGER_NAV_KEY);
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export function formatCents(cents, currency = 'CAD') {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency
    }).format(n / 100);
  } catch (_) {
    return `$${(n / 100).toFixed(2)}`;
  }
}

export function heldDepositCents(deposit) {
  if (!deposit) return 0;
  return Math.max(
    0,
    Number(deposit.collectedAmountCents || 0) -
      Number(deposit.appliedAmountCents || 0) -
      Number(deposit.refundedAmountCents || 0)
  );
}
